/** biome-ignore-all lint/performance/noDelete: <explanation> */

import { Analytics } from '@cobliteam/analytics';
import type { Preferences } from '@cobliteam/common-types';
import { useUser } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { Button } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import qs from 'query-string';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DIALOG_CLOSED_KEY = 'hide-wrapper-2025';
const IFRAME_ORIGIN = 'https://main.dpvupxdvtpphd.amplifyapp.com';
// const IFRAME_ORIGIN = 'http://localhost:3001';

const DialogWrapper2025 = () => {
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const [expanded, setExpanded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const displayedTrackedRef = useRef(false);
  const { session } = useUser();
  const [fleetData, setFleetData] = useState<any>(null);
  const fleetId = session?.fleet.id;
  const { wrapper2025 } = useFeatureFlags();

  const fetchData = useCallback(async () => {
    const response = await fetch(`${IFRAME_ORIGIN}/api/fleet`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${session?.authenticationToken}`,
      },
    });
    const data = await response.json();
    if (data?.fleet_id) setFleetData(data);
  }, [session?.authenticationToken]);

  useEffect(() => {
    if (fleetId) {
      fetchData();
    }
  }, [fleetId, fetchData]);

  useEffect(() => {
    if (fleetData && wrapper2025 && !preferences?.[DIALOG_CLOSED_KEY] && !displayedTrackedRef.current) {
      Analytics.track('Wrapper 2025 Dialog Displayed');
      displayedTrackedRef.current = true;
    }
  }, [fleetData, wrapper2025, preferences]);

  const handleCollapse = () => {
    Analytics.track('Wrapper 2025 Dialog Collapsed');
    setExpanded(false);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    Analytics.track('Wrapper 2025 Dialog Dismissed');
    setPreferences({
      ...preferences,
      [DIALOG_CLOSED_KEY]: true,
    } as Preferences & Record<string, any>);
  };

  const iframeUrl = useMemo(() => {
    //parse fleetData to query params
    const newFleetData = {
      ...fleetData,
      id: fleetData?.FLEET_ID,
    };

    delete newFleetData?.FLEET_ID;

    return `${IFRAME_ORIGIN}?${qs.stringify(newFleetData)}`;
  }, [fleetData]);

  if (preferences?.[DIALOG_CLOSED_KEY]) {
    return null;
  }

  if (!fleetData) {
    return null;
  }

  if (!wrapper2025) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        key='dialog-wrapper'
        initial='hidden'
        animate={expanded ? 'expanded' : 'collapsed'}
        exit='hidden'
        variants={{
          hidden: {
            bottom: -100,
            left: '50%',
            x: '-50%',
            width: 420,
            height: 64,
            borderRadius: 12,
            backgroundColor: '#1C1C1E',
            opacity: 0,
          },
          collapsed: {
            bottom: 32,
            left: '50%',
            x: '-50%',
            width: 420,
            height: 64,
            borderRadius: 12,
            backgroundColor: '#1C1C1E',
            opacity: 1,
          },
          expanded: {
            bottom: 0,
            left: 0,
            x: 0,
            width: '100%',
            height: '100%',
            borderRadius: 0,
            backgroundColor: '#ffffff',
            opacity: 1,
          },
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{ position: 'fixed', zIndex: 999999, overflow: 'hidden' }}
        className='shadow-2xl'
      >
        <AnimatePresence mode='wait'>
          {expanded && fleetData ? (
            <motion.div key='expanded' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, delay: 0.1 }} className='h-full w-full'>
              <Button
                onClick={handleCollapse}
                className='absolute top-3 right-3 z-[9999999] cursor-pointer rounded-full bg-white p-2 shadow-md'
                icon={<Icon name='x' />}
                variant='ghost'
                size='sm'
              />
              <iframe ref={iframeRef} src={iframeUrl} className='h-full w-full border-0' title='Retrospectiva 2025' allow='fullscreen' />
            </motion.div>
          ) : (
            <motion.div
              key='toast'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => {
                Analytics.track('Wrapper 2025 Dialog Expanded');
                setExpanded(true);
              }}
              className='absolute inset-0 flex cursor-pointer items-center justify-between px-4'
            >
              <div className='flex items-center gap-3'>
                <motion.div whileHover={{ y: -2, rotate: 12 }} transition={{ type: 'spring', stiffness: 400, damping: 10 }}>
                  <Icon name='rocket' size='md' className='text-white' />
                </motion.div>
                <div className='flex flex-col'>
                  <span className='font-bold text-sm text-white leading-tight'>Retrospectiva 2025!</span>
                  <span className='text-gray-400 text-xs'>Confira seus resultados do ano</span>
                </div>
              </div>

              <div className='flex items-center gap-3'>
                <motion.button type='button' whileHover='hover' className='flex items-center gap-1 rounded-md bg-[#333333] px-3 py-1.5 font-medium text-white text-xs'>
                  <motion.span variants={{ hover: { scale: 1.05 } }} transition={{ duration: 0.2 }}>
                    Ver agora
                  </motion.span>
                  <motion.div variants={{ hover: { x: 3 } }} transition={{ duration: 0.2 }}>
                    <Icon name='arrow-right' size='xs' />
                  </motion.div>
                </motion.button>
                <div onClick={handleDismiss} className='text-gray-400 hover:text-white'>
                  <Icon name='x' size='sm' />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default memo(DialogWrapper2025);
